// The Twetch API, as /alkanes speaks it.
//
// The social tab reads Twetch's public API directly. OW-only capabilities
// (account-on-first-post, BTC sponsorship eligibility, sponsored writes) use
// the same-origin server bridge so Vercel's signed OIDC identity never enters
// the browser. Every non-2xx here carries a JSON `{error}` body
// whose message is the ONLY honest thing to show a user, so the error type
// keeps both the status and that message instead of flattening to "failed".

import { owContextHeaders } from './ow-context-headers'

export const TWETCH_API = 'https://api.twetch.com'
export const TWETCH_BRIDGE_API = '/api/twetch'
export const TWETCH_WEB = 'https://twetch.com'

/// A Twetch refusal: HTTP status plus the server's own `{error}` message.
export class TwetchError extends Error {
	readonly status: number

	constructor(status: number, message: string) {
		super(message)
		this.name = 'TwetchError'
		this.status = status
	}
}

export async function twetchJson<T>(path: string, init?: RequestInit): Promise<T> {
	return twetchJsonAt<T>(TWETCH_API, path, init)
}

export async function twetchBridgeJson<T>(path: string, init?: RequestInit): Promise<T> {
	return twetchJsonAt<T>(TWETCH_BRIDGE_API, path, init)
}

async function twetchJsonAt<T>(base: string, path: string, init?: RequestInit): Promise<T> {
	const request =
		base === TWETCH_BRIDGE_API
			? {
					...init,
					headers: {
						...owContextHeaders(),
						...(init?.headers as Record<string, string> | undefined)
					}
			  }
			: init
	const res = await fetch(`${base}${path}`, request)
	if (!res.ok) {
		throw new TwetchError(res.status, (await errorDetail(res)) || `HTTP ${res.status} for ${path}`)
	}
	return res.json() as Promise<T>
}

/// The server's own words for a refusal. Twetch answers `{error}` JSON, but a
/// body the API could not even parse comes back from its extractor as plain
/// text ("Failed to deserialize the JSON body into the target type: aip.signature:
/// invalid type: map, expected a string …"). That sentence IS the diagnosis, so it
/// is kept instead of being flattened to a bare status code. Markup (a CDN or
/// proxy error page) is never shown as a message.
async function errorDetail(res: Response): Promise<string> {
	let text = ''
	try {
		text = (await res.text()).trim()
	} catch {
		return ''
	}
	try {
		const body = JSON.parse(text) as { error?: unknown; message?: unknown }
		const detail = body?.error ?? body?.message
		return typeof detail === 'string' ? detail : ''
	} catch {
		return text.startsWith('<') ? '' : text.slice(0, 240)
	}
}

// ── feed shapes (as the live API returns them, camelCase) ──────────────────

interface TwetchLinkPreview {
	url?: string | null
	title?: string | null
	description?: string | null
	image?: string | null
}

export interface TwetchPost {
	id: number
	txid?: string | null
	userId: number
	content?: string | null
	smartTags?: TwetchSmartTag[]
	contentType?: string | null
	/// True when `content` was trimmed server-side — the full text lives at the
	/// permalink, and the row must say so rather than pass the cut as the post.
	contentTruncated?: boolean
	type?: string | null
	replyPostId?: number | null
	numLikes?: number
	numBranches?: number
	numReplies?: number
	/// JSON array of B:// media refs (`b://<txid>@<vout>` or bare txids).
	files?: string | null
	linkPreview?: TwetchLinkPreview | null
	createdAtMs?: number
	postedAtMs?: number
}

export interface TwetchSmartTag {
	/// `alkanes` for AMM tokens; `ordinalswallet` for any OW market (see
	/// collection-tag.ts). Open-ended on Twetch's side.
	namespace: string
	id: string
	symbol: string
}

export interface TwetchUser {
	id: number
	name?: string | null
	icon?: string | null
	verifiedIcon?: string | null
	/// The wallet this account is ROOTED in, when it is not a Twetch seed —
	/// for an account made here, the Bitcoin address it posts from.
	externalIdentity?: { algorithm: string; address: string } | null
}

export interface TwetchFeedPage {
	data: TwetchPost[]
	users: Record<string, TwetchUser>
	/// post id → quoted post id, hydrated by `quotedPosts`.
	quotes?: Record<string, number>
	quotedPosts?: Record<string, TwetchPost>
	/// reply target posts by id, for "Replying to …" context lines.
	replyPosts?: Record<string, TwetchPost>
	/// media cache_id ("<txid>" | "<txid>-o<vout>") → [width, height].
	mediaDims?: Record<string, [number, number]>
	nextCursor?: string | null
}

const FEED_PAGE_SIZE = 25

/// One page of posts carrying a server-validated Alkanes smart tag. This is an
/// exact indexed feed, not a text search for whatever happens to contain `$`.
export async function fetchFeedPage(
	cursor?: string | null,
	assetId?: string | null,
	namespace = 'alkanes'
): Promise<TwetchFeedPage> {
	const params = new URLSearchParams({
		namespace,
		limit: String(assetId ? 100 : FEED_PAGE_SIZE)
	})
	if (assetId) {
		params.set('id', assetId)
	}
	if (cursor) {
		params.set('cursor', cursor)
	}
	return twetchJson<TwetchFeedPage>(
		`/v1/feed/smart-tags?${params.toString()}`,
		Date.now() < feedFreshUntil ? { cache: 'reload' } : undefined
	)
}

/// One page of the posts written by the people `userId` follows.
export async function fetchFollowingPage(
	userId: number,
	cursor?: string | null
): Promise<TwetchFeedPage> {
	const params = new URLSearchParams({ userId: String(userId), limit: String(FEED_PAGE_SIZE) })
	if (cursor) {
		params.set('cursor', cursor)
	}
	return twetchJson<TwetchFeedPage>(
		`/v1/feed/following?${params.toString()}`,
		Date.now() < feedFreshUntil ? { cache: 'reload' } : undefined
	)
}

/// The feed is served `max-age=10, stale-while-revalidate=30`, so a refetch
/// right after the user's own post is answered by the browser's cache — the
/// post they just made is missing until a reload. For a short window after a
/// write, feed reads skip that cache (same URL, so the CDN object is shared).
let feedFreshUntil = 0
export function expectFreshFeed(windowMs = 20_000): void {
	feedFreshUntil = Date.now() + windowMs
}

/// Whether the paymaster will sponsor posts for this user (public probe).
/// The composer disables honestly on `false` — it never fakes a working door.
export async function fetchSponsorEligibility(
	userId: number,
	external?: { algorithm: string; address: string } | null
): Promise<{ eligible: boolean }> {
	const params = new URLSearchParams()
	if (external) {
		params.set('algorithm', external.algorithm)
		params.set('address', external.address)
	}
	const query = params.size ? `?${params.toString()}` : ''
	return twetchBridgeJson<{ eligible: boolean }>(`/v1/sponsor/eligibility/${userId}${query}`)
}
